import React, { useState, useEffect, useRef } from 'react';
import { cn } from '../../lib/utils';

const DropdownMenu = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      {React.Children.map(children, (child) => {
        if (child.type === DropdownMenuTrigger) {
          return React.cloneElement(child, {
            onClick: () => setIsOpen(!isOpen),
          });
        }
        if (child.type === DropdownMenuContent) {
          return React.cloneElement(child, {
            isOpen,
            onClose: () => setIsOpen(false),
          });
        }
        return child;
      })}
    </div>
  );
};

const DropdownMenuTrigger = ({ children, onClick, asChild, ...props }) => {
  if (asChild) {
    return React.cloneElement(children, {
      ...props,
      onClick,
    });
  }

  return (
    <button {...props} onClick={onClick}>
      {children}
    </button>
  );
};

const DropdownMenuContent = ({ children, isOpen, onClose, align = 'start', className = '', ...props }) => {
  if (!isOpen) return null;

  const alignmentClasses = {
    start: 'left-0',
    end: 'right-0',
    center: 'left-1/2 transform -translate-x-1/2',
  };

  return (
    <div
      className={cn(
        'absolute mt-2 w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none z-50',
        alignmentClasses[align],
        className
      )}
      {...props}
    >
      <div className="py-1">
        {React.Children.map(children, (child) => {
          if (child.type === DropdownMenuItem) {
            return React.cloneElement(child, {
              onClose,
            });
          }
          return child;
        })}
      </div>
    </div>
  );
};

const DropdownMenuLabel = ({ children, className = '', ...props }) => {
  return (
    <div
      className={cn(
        'px-3 py-2 text-sm font-semibold text-gray-900',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

const DropdownMenuSeparator = ({ className = '', ...props }) => {
  return (
    <div
      className={cn(
        'my-1 h-px bg-gray-200',
        className
      )}
      {...props}
    />
  );
};

const DropdownMenuItem = ({ children, onClick, onClose, asChild, className = '', ...props }) => {
  const handleClick = () => {
    onClick && onClick();
    onClose && onClose();
  };

  if (asChild) {
    return React.cloneElement(children, {
      ...props,
      className: cn(
        'text-gray-900 group flex w-full items-center rounded-md px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer transition-colors',
        className,
        children.props.className
      ),
      onClick: handleClick,
    });
  }

  return (
    <div
      className={cn(
        'text-gray-900 group flex w-full items-center rounded-md px-3 py-2 text-sm hover:bg-gray-100 cursor-pointer transition-colors',
        className
      )}
      onClick={handleClick}
      {...props}
    >
      {children}
    </div>
  );
};

DropdownMenu.Trigger = DropdownMenuTrigger;
DropdownMenu.Content = DropdownMenuContent;
DropdownMenu.Label = DropdownMenuLabel;
DropdownMenu.Separator = DropdownMenuSeparator;
DropdownMenu.Item = DropdownMenuItem;

export default DropdownMenu;
export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
};
